/**
 * CBSRowCognaraCell — AG Grid cell renderer for the CBS EVM Breakdown table.
 * A small sparkle icon per row; on click opens a popover with Cognara's
 * integrated schedule + cost narrative for the selected CBS element
 * (parent or leaf): SPI/CPI analysis, EAC view, delay impact, overrun
 * reasons, emerging variances, and schedule/resource/cost recommendations.
 *
 * Cached server-side per row fingerprint; second click is instant.
 */
import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sparkles, Loader2, RefreshCw, TrendingDown, TrendingUp, AlertTriangle, Wrench, Clock, IndianRupee } from 'lucide-react';
import api from '@/lib/api';
import CognaraRaidPushInline from '@/components/scheduling/CognaraRaidPushInline';

const SEVERITY = {
  CRITICAL: { fg: '#dc2626', bg: 'rgba(220,38,38,0.12)' },
  HIGH:     { fg: '#ea580c', bg: 'rgba(234,88,12,0.12)' },
  MEDIUM:   { fg: '#ca8a04', bg: 'rgba(202,138,4,0.12)' },
  LOW:      { fg: '#10b981', bg: 'rgba(16,185,129,0.12)' },
};

function localHint(d) {
  const spi = Number(d?.spi || 1), cpi = Number(d?.cpi || 1);
  if (spi < 0.7 || cpi < 0.7) return 'CRITICAL';
  if (spi < 0.85 || cpi < 0.85) return 'HIGH';
  if (spi < 0.98 || cpi < 0.98) return 'MEDIUM';
  return 'LOW';
}

export default function CBSRowCognaraCell(props) {
  const { projectId, data } = props;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const tint = SEVERITY[localHint(data)] || SEVERITY.MEDIUM;
  const disabled = !projectId || !data?.costCode;

  const load = async (force = false) => {
    if (disabled) return;
    setLoading(true); setError(null);
    try {
      const r = await api.post(
        `/v1/projects/${projectId}/evm/cbs/${encodeURIComponent(data.costCode)}/cognara-analysis${force ? '?force=true' : ''}`,
        data, { timeout: 45000 }
      );
      setPayload(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'AI failed');
    } finally { setLoading(false); }
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (next && !payload && !loading) load(false);
  };

  if (disabled) return <span className="text-slate-500 text-xs">—</span>;

  const a = payload?.analysis;
  const sev = a ? (SEVERITY[a.severity] || tint) : tint;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid={`cbs-cognara-btn-${data.costCode}`}
          onClick={(e) => e.stopPropagation()}
          title="Row-level Cognara analysis"
          className="inline-flex items-center justify-center rounded transition-transform hover:scale-110"
          style={{ width: 22, height: 22, background: tint.bg, border: `1px solid ${tint.fg}55`, color: tint.fg }}
        >
          <Sparkles className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" side="left" className="w-[440px] p-0 border-0"
        data-testid={`cbs-cognara-popover-${data.costCode}`}
        style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <Sparkles className="w-3.5 h-3.5" style={{ color: '#06b6d4' }} />
          <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-primary)' }}>
            CBS Cognara · {data.costCode}
          </span>
          {a && (
            <span className="ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold"
                  style={{ background: sev.bg, color: sev.fg }}>{a.severity}</span>
          )}
        </div>

        <div className="p-3 space-y-2 max-h-[70vh] overflow-y-auto">
          <div className="text-[10px] font-mono opacity-60" style={{ color: 'var(--text-muted)' }}>
            {data.costCodeName} · SPI {Number(data.spi||0).toFixed(2)} · CPI {Number(data.cpi||0).toFixed(2)} · {Number(data.physicalProgressPct||0).toFixed(1)}%
          </div>

          {loading && (
            <div className="flex items-center gap-2 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> <span>Asking Cognara…</span>
            </div>
          )}
          {error && <div className="text-xs py-2" style={{ color: '#dc2626' }}>{error}</div>}

          {a && !loading && (
            <>
              <div className="rounded p-2" style={{ background: 'var(--surface-bg)', border: `1px solid ${sev.fg}44` }}>
                <div className="text-[12px] font-semibold leading-snug" style={{ color: 'var(--text-primary)' }}>
                  {a.headline}
                </div>
              </div>

              <Panel icon={<Clock className="w-3 h-3" style={{ color: '#0ea5e9' }} />} title="Schedule Performance">
                {a.schedule_analysis}
              </Panel>
              <Panel icon={<IndianRupee className="w-3 h-3" style={{ color: '#a855f7' }} />} title="Cost Performance">
                {a.cost_analysis}
              </Panel>
              <Panel icon={<TrendingUp className="w-3 h-3" style={{ color: '#22c55e' }} />} title="EAC View">
                {a.eac_view}
              </Panel>
              <Panel icon={<TrendingDown className="w-3 h-3" style={{ color: '#ef4444' }} />} title="Delay / Recovery Impact">
                {a.delay_impact}
              </Panel>

              {a.overrun_reasons?.length > 0 && (
                <Panel icon={<AlertTriangle className="w-3 h-3" style={{ color: '#f97316' }} />}
                       title="Reasons for Overrun" testid="cbs-overrun-reasons">
                  <ul className="list-disc pl-4 space-y-0.5">
                    {a.overrun_reasons.map((r, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span className="flex-1">{r}</span>
                        <CognaraRaidPushInline
                          projectId={projectId}
                          section="overrun_reasons"
                          bullet={r}
                          task={{ wbs_code: data.costCode, name: data.costCodeName }}
                          riskLevel={a.severity}
                          sourceKind="cbs-row"
                          sourceCode={data.costCode}
                          sourceName={data.costCodeName}
                          raidCategory="COST"
                        />
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}

              {a.emerging_variances?.length > 0 && (
                <Panel icon={<AlertTriangle className="w-3 h-3" style={{ color: '#eab308' }} />}
                       title="Emerging Variances">
                  <ul className="list-disc pl-4 space-y-0.5">
                    {a.emerging_variances.map((r, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span className="flex-1">{r}</span>
                        <CognaraRaidPushInline
                          projectId={projectId}
                          section="emerging_variances"
                          bullet={r}
                          task={{ wbs_code: data.costCode, name: data.costCodeName }}
                          riskLevel={a.severity}
                          sourceKind="cbs-row"
                          sourceCode={data.costCode}
                          sourceName={data.costCodeName}
                          raidCategory="COST"
                        />
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}

              <div className="rounded p-2" style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }} data-testid="cbs-recs">
                <div className="text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1" style={{ color: sev.fg }}>
                  <Wrench className="w-3 h-3" /> Recommended Actions
                </div>
                {['schedule','resource','cost'].map(k => (
                  (a.recommendations?.[k]?.length || 0) > 0 && (
                    <div key={k} className="mb-1">
                      <div className="text-[9px] uppercase opacity-60" style={{ color: 'var(--text-muted)' }}>{k}</div>
                      <ul className="text-[11px] list-disc pl-4" style={{ color: 'var(--text-primary)' }}>
                        {a.recommendations[k].map((r,i) => (
                          <li key={i} className="flex items-start gap-1">
                            <span className="flex-1">{r}</span>
                            <CognaraRaidPushInline
                              projectId={projectId}
                              section={`recommendation_${k}`}
                              bullet={r}
                              task={{ wbs_code: data.costCode, name: data.costCodeName }}
                              riskLevel={a.severity}
                              sourceKind="cbs-row"
                              sourceCode={data.costCode}
                              sourceName={data.costCodeName}
                              raidCategory={k === 'schedule' ? 'SCHEDULE' : k === 'cost' ? 'COST' : 'RESOURCE'}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                ))}
              </div>

              <div className="flex items-center gap-2 pt-1">
                <div className="text-[9px] opacity-50 flex-1" style={{ color: 'var(--text-muted)' }}>
                  Confidence: {Math.round((a.confidence || 0) * 100)}% · Powered by Cognara
                </div>
                <button onClick={(e) => { e.stopPropagation(); load(true); }}
                        title="Re-ask AI"
                        className="p-1 rounded hover:opacity-80"
                        style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}
                        data-testid={`cbs-cognara-refresh-${data.costCode}`}>
                  <RefreshCw className="w-3 h-3" />
                </button>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Panel({ icon, title, children, testid }) {
  return (
    <div className="rounded p-2" style={{ background: 'var(--surface-bg)', border: '1px solid var(--border-subtle)' }}
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1"
           style={{ color: 'var(--text-muted)' }}>
        {icon} {title}
      </div>
      <div className="text-[11px] leading-snug" style={{ color: 'var(--text-primary)' }}>
        {children}
      </div>
    </div>
  );
}
